import { useEffect, useState } from 'react'

import { Checkbox, Avatar, Tooltip, Box } from '@mantine/core'
import dayjs from 'dayjs'

import { Link, routes } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { UPDATE_TASK } from 'src/graphql/tasks.graphql'
import { TaskStatus, TaskType } from 'src/graphql/types/tasks'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconPhone from 'src/icons/IconPhone'
import IconUsers from 'src/icons/IconUsers'
import { buildFullName } from 'src/lib/formatters'
import { unformatPhoneNumber } from 'src/lib/phone.utils'
import { PRIORITY_CONFIG } from 'src/lib/taskPriority'
import { cn } from 'src/utils'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'

const SimplifiedTaskRow = ({
  task,
  onClick = null,
  canMarkAsCompleted = true,
  showApplicantLink = true,
  onStatusChange = null,
}) => {
  const { handleOpen } = useTaskModalContext()
  const phoneDeviceContext = usePhoneDevice()
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const assignee = task.assignees.length > 0 ? task.assignees[0] : null
  const [isCompleted, setIsCompleted] = useState(
    task.status === TaskStatus.COMPLETED
  )

  // Re-sync the optimistic state if the task's status changes (e.g. after a
  // refetch or an edit elsewhere), so the checkbox and overdue badge don't go
  // stale on the same taskId.
  useEffect(() => {
    setIsCompleted(task.status === TaskStatus.COMPLETED)
  }, [task.taskId, task.status])

  const isOverdue =
    !!task.dueDate && !isCompleted && dayjs(task.dueDate).isBefore(dayjs())

  const handleComplete = (e) => {
    e.stopPropagation()
    setIsCompleted(!isCompleted)
    setTimeout(() => {
      updateTask({
        variables: {
          taskId: task.taskId,
          input: {
            status: isCompleted ? TaskStatus.NOT_STARTED : TaskStatus.COMPLETED,
          },
        },
      })
    }, 300)
  }

  const [updateTask] = useMutation(UPDATE_TASK, {
    refetchQueries: ['Notifications'],
    onCompleted: () => {
      onStatusChange?.()
    },
    onError: () => {
      // Roll the optimistic toggle back to the server's value on failure.
      setIsCompleted(task.status === TaskStatus.COMPLETED)
      toast('Error updating task', 'error')
    },
  })

  return (
    <Tooltip
      label={
        <div className="flex max-w-xs flex-col gap-1.5 whitespace-normal">
          <div className="break-words text-xs font-medium">{task.title}</div>
          {task.description && (
            <div className="break-words text-xs text-doubleNickel-gray-400">
              {task.description}
            </div>
          )}
          {(task.dueDate || task.reminderDate) && (
            <div className="mt-1 flex flex-col gap-1 border-t border-doubleNickel-gray-300 pt-1.5">
              {task.dueDate && (
                <div className="text-xs text-doubleNickel-gray-400">
                  Due: {dayjs(task.dueDate).format('MMM D, YYYY h:mm A')}
                </div>
              )}
              {task.reminderDate && (
                <div className="text-xs text-doubleNickel-gray-400">
                  Reminder:{' '}
                  {dayjs(task.reminderDate).format('MMM D, YYYY h:mm A')}
                </div>
              )}
            </div>
          )}
          {task.applicant && (
            <div className="mt-1 flex flex-col gap-1 border-t border-doubleNickel-gray-300 pt-1.5">
              <div className="text-xs text-doubleNickel-gray-400">
                Applicant: {buildFullName(task.applicant)}
              </div>
            </div>
          )}
        </div>
      }
      withArrow
      position="top"
    >
      <Box
        key={task.taskId}
        className={cn(
          'border-bg-doubleNickel-gray-600 flex min-h-8 max-w-full cursor-pointer flex-row items-center justify-between gap-2 px-2 text-xs text-doubleNickel-gray-800 transition-opacity duration-300 hover:bg-doubleNickel-gray-50 ',
          isCompleted && 'opacity-50'
        )}
        onClick={() => {
          if (onClick) {
            onClick()
          } else {
            handleOpen({ id: task.taskId })
          }
        }}
      >
        <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
          {canMarkAsCompleted && (
            <Box onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
              <Checkbox
                size="xs"
                checked={isCompleted}
                onChange={handleComplete}
              />
            </Box>
          )}
          <div
            className={cn('min-w-0 truncate', isCompleted && 'line-through')}
          >
            {task.title.length > 60
              ? `${task.title.substring(0, 60)}...`
              : task.title}
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-row items-center gap-2">
          {isOverdue && (
            <Box className="flex flex-shrink-0 items-center gap-1 rounded bg-doubleNickel-error-50 px-1.5 py-0.5 text-[10px] font-normal text-doubleNickel-error-600">
              <IconAlertTriangle className="h-2.5 w-2.5 fill-none stroke-doubleNickel-error-600" />
              Overdue
            </Box>
          )}
          {task.type === TaskType.CALL &&
            task.applicant?.phone && (
              <Tooltip label="Call applicant" withArrow position="top" className="text-xs">
                <button
                  type="button"
                  aria-label="Call applicant"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (phoneDeviceContext?.phoneDevice?.isBusy) return
                    const applicantFullName = buildFullName(task.applicant)
                    dispatchPhoneAction?.({
                      type: PhoneDeviceActionType.CALL,
                      payload: {
                        applicantId: task.applicant.applicantId,
                        phone: unformatPhoneNumber(task.applicant.phone),
                        applicantFullName,
                      },
                    })
                  }}
                  className="group flex h-6 w-6 items-center justify-center rounded hover:bg-doubleNickel-brand-25" // pragma: allowlist secret
                >
                  <IconPhone className="h-3 w-3 fill-none stroke-doubleNickel-gray-500 group-hover:stroke-doubleNickel-brand-600" /> {/* pragma: allowlist secret */}
                </button>
              </Tooltip>
            )}
          {showApplicantLink && task.applicant && (
            <Tooltip
              label={`Go to ${buildFullName(task.applicant)}'s profile`}
              withArrow
              position="top"
              className="text-xs"
            >
              <Box
                component={Link}
                to={routes.applicantDetails({
                  id: task.applicant.applicantId,
                  tab: 'application',
                })}
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0 hover:cursor-pointer"
              >
                <IconUsers className="h-3 w-3 fill-none stroke-doubleNickel-brand-500" />
              </Box>
            </Tooltip>
          )}
          {(() => {
            const priorityConfig = PRIORITY_CONFIG[task.priority]
            if (!priorityConfig) return null
            const { Icon, label, color } = priorityConfig
            return (
              <Tooltip
                label={label}
                withArrow
                position="top"
                className="text-xs"
              >
                <Box className={cn('flex flex-shrink-0 items-center', color)}>
                  <Icon className="h-4 w-4" />
                </Box>
              </Tooltip>
            )
          })()}
          {assignee && (
            <Avatar.Group spacing="0">
              <Tooltip
                key={assignee.employeeId}
                label={`${assignee.firstName} ${assignee.lastName}`}
                position="top"
                className="text-xs"
              >
                <Avatar color="blue" size="sm" radius="sm">
                  {assignee.firstName.charAt(0)}
                  {assignee.lastName.charAt(0)}
                </Avatar>
              </Tooltip>
              {task.assignees.length > 1 && (
                <Tooltip
                  withArrow
                  label={
                    <>
                      {task.assignees
                        .filter((a) => a.employeeId !== assignee.employeeId)
                        .map((assignee) => (
                          <div key={assignee.employeeId}>
                            {assignee.firstName} {assignee.lastName}
                          </div>
                        ))}
                    </>
                  }
                  position="top"
                  className="text-xs"
                >
                  <Avatar size="sm" radius="sm">
                    +{task.assignees.length - 1}
                  </Avatar>
                </Tooltip>
              )}
            </Avatar.Group>
          )}
        </div>
      </Box>
    </Tooltip>
  )
}

export default SimplifiedTaskRow
